import React, { useRef, useEffect, useCallback } from 'react';
import styles from '../Input/input.module.css';
import selectStyles from './select.module.css';
import ReactSelect, { components } from 'react-select';
import CreatableReactSelect from 'react-select/creatable';
import { useNavigate } from 'react-router-dom';
import icon from '../../brand/generated/buttonIcons/plus.svg';
import { colors } from '../../constants/colors';
import Hint from '../Hint';

const NoOptionsMessage = (props) => {
  return (
    <components.NoOptionsMessage {...props}>
      <AddUserOption />
    </components.NoOptionsMessage>
  );
};

const AddOptionRow = ({ text, onClick, onMouseDown }) => (
  <div
    style={{ display: 'flex', alignItems: 'flex-start', columnGap: '12px', cursor: 'pointer', padding: 10 }}
    onClick={onClick}
    onMouseDown={onMouseDown}
  >
    <img src={icon} alt="Icon" />
    <p style={{ textDecoration: 'underline', color: colors.cameo }}>{text}</p>
  </div>
);

const AddUserOption = () => {
  const navigate = useNavigate();
  return <AddOptionRow text="Добавить нового клиента" onClick={() => navigate('/client/create')} />;
};

// Последняя строка списка «Добавить ...»: менеджер сначала смотрит, есть ли
// нужное значение, и только потом заводит новое. Меню закрываем руками —
// оно висит в портале с z-index выше модалки, которую открывает обработчик.
const AddDictionaryOption = ({ selectProps }) => (
  <AddOptionRow
    text={selectProps.addOptionLabel}
    // preventDefault на mousedown: иначе фокус уходит с инпута, меню
    // закрывается ещё до click и обработчик не успевает сработать.
    onMouseDown={(e) => e.preventDefault()}
    onClick={() => {
      document.activeElement?.blur?.();
      selectProps.onAddOption();
    }}
  />
);

const MenuListWithSentinel = ({ children, innerRef: innerRefProp, selectProps, ...props }) => {
  const sentinelRef = useRef(null);
  const containerRef = useRef(null);
  const onBottomRef = useRef(null);
  const prevIsLoadingRef = useRef(null);
  const optionsCountRef = useRef(0);

  onBottomRef.current = selectProps?.onScrollToBottom;
  optionsCountRef.current = selectProps?.options?.length ?? 0;

  const setContainerRef = useCallback(
    (el) => {
      containerRef.current = el;
      if (typeof innerRefProp === 'function') innerRefProp(el);
      else if (innerRefProp && 'current' in innerRefProp) innerRefProp.current = el;
    },
    [innerRefProp]
  );

  const tryLoadMore = useCallback(() => {
    const sentinel = sentinelRef.current;
    const container = containerRef.current;
    if (!sentinel || !container || !onBottomRef.current) return;
    // Пустому списку нечего догружать. Сентинел при этом висит в самом верху
    // контейнера, то есть «низ достигнут» всегда — и onScrollToBottom дёргался
    // бы после каждой загрузки, зацикливая справочник, который вернул [].
    // Первую страницу тянет onMenuOpen, а не этот путь.
    if (!optionsCountRef.current) return;
    const containerRect = container.getBoundingClientRect();
    const sentinelRect = sentinel.getBoundingClientRect();
    if (sentinelRect.top < containerRect.bottom + 100) {
      onBottomRef.current();
    }
  }, []);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const container = containerRef.current;
    if (!sentinel || !container || !onBottomRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) tryLoadMore();
      },
      { root: container, rootMargin: '0px 0px 100px 0px', threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [tryLoadMore]);

  const isLoading = selectProps?.isLoading;
  const optionsCount = selectProps?.options?.length ?? 0;

  useEffect(() => {
    const prevIsLoading = prevIsLoadingRef.current;
    prevIsLoadingRef.current = isLoading;
    if (prevIsLoading === true && !isLoading) {
      tryLoadMore();
    }
  }, [isLoading, optionsCount, tryLoadMore]);

  const showBottomLoader = isLoading && optionsCount > 0;

  return (
    <components.MenuList {...props} selectProps={selectProps} innerRef={setContainerRef}>
      {children}
      {selectProps?.isAddClient && <AddUserOption />}
      {selectProps?.onAddOption && <AddDictionaryOption selectProps={selectProps} />}
      {showBottomLoader && (
        <div className={selectStyles.menuLoader}>
          <div className={selectStyles.menuLoaderSpinner} />
        </div>
      )}
      <div ref={sentinelRef} />
    </components.MenuList>
  );
};

const useIsTablet = () => {
  const [isTablet, setIsTablet] = React.useState(window.innerWidth <= 1250);
  React.useEffect(() => {
    const onResize = () => setIsTablet(window.innerWidth <= 1250);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isTablet;
};

const Select = ({
  style,
  title,
  label,
  placeholder,
  options,
  state,
  defaultValue,
  handleSetState,
  handleSetSelect,
  isSearchable = false,
  onFocus,
  isAddClient,
  isMulti,
  disabled,
  onChange,
  value,
  error,
  required,
  isLoading = false,
  hint,
  onChangeCustom,
  creatable,
  ...props
}) => {
  const isTablet = useIsTablet();

  const customTheme = (theme) => {
    return {
      ...theme,
      borderRadius: '0px',
      colors: {
        ...theme.colors,
        primary: colors.cameo,
        primary25: '#F2F2F2',
        primary50: '#F2F2F2',
      },
    };
  };

  // creatable — опционально: позволяет ввести значение, которого нет в
  // options (react-select/creatable), например когда справочник ещё не
  // содержит нужный вариант, а бэк принимает произвольный текст (title) и
  // сам заводит запись при сохранении. Ничего не меняет для существующих
  // мест использования Select, где этот проп не передаётся
  const SelectComponent = creatable ? CreatableReactSelect : ReactSelect;

  React.useEffect(() => {}, [defaultValue]);
  return (
    <div className={styles.customInput}>
      <p>
        {title || label} {required && <span style={{ color: colors.red }}>*</span>}
        {hint && <Hint text={hint} />}
      </p>
      <SelectComponent
        components={{ MenuList: MenuListWithSentinel }}
        isSearchable={isSearchable}
        defaultValue={defaultValue}
        value={value}
        onInputChange={(e, meta) => {
          // meta нужен, чтобы отличить реальный ввод от сброса строки при
          // закрытии меню — иначе селекты с серверным поиском перезапрашивают
          // список после каждого выбора. Старые потребители второй аргумент
          // просто игнорируют.
          if (handleSetSelect) handleSetSelect(e, meta);
        }}
        options={options}
        onChange={(val) => {
          if (handleSetState) {
            handleSetState(val);
          }
          if (onChange) {
            onChange(val);
          }
          if (onChangeCustom) {
            onChangeCustom(val);
          }
        }}
        placeholder={placeholder}
        classNames={{ control: (state) => (isMulti ? 'custom-select-multi' : 'custom-select') }}
        styles={{
          control: (baseStyles, state) => ({
            ...baseStyles,
            borderColor: state.isFocused ? colors.cameo : error ? colors.red : colors.alto,
            backgroundColor: state.isDisabled ? `${colors.disabled} !important` : colors.backgroung,
            minHeight: isTablet ? '52px' : '44px',
            maxHeight: isMulti ? 'auto' : (isTablet ? '52px' : '44px'),
            boxShadow: '0px',
          }),
          option: (base) => ({ ...base, fontSize: '16px', padding: isTablet ? '12px 14px' : '10px 12px' }),
          singleValue: (base) => ({ ...base, fontSize: '16px' }),
          placeholder: (base) => ({ ...base, fontSize: '16px' }),
          input: (base) => ({ ...base, fontSize: '16px' }),
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        }}
        onScrollToBottom={onFocus}
        isAddClient={isAddClient}
        theme={customTheme}
        isMulti={isMulti ? isMulti : false}
        isDisabled={disabled}
        menuPortalTarget={document.body}
        menuPlacement="auto"
        isLoading={isLoading}
        onMenuOpen={() => {
          onFocus && onFocus();
        }}
        {...props}
      />
    </div>
  );
};

export default Select;
